import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Alert,
  useTheme,
  useMediaQuery,
  Tab,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  leaderboardAPI,
  coursesAPI,
  cachedLeaderboardAPI,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import LeaderboardFilters from '../components/Leaderboard/LeaderboardFilters';
import UsersList from '../components/Leaderboard/UsersList';
import PersonalRanking from '../components/Leaderboard/PersonalRanking';
import Competitions from '../components/Leaderboard/Competitions';
import Challenges from '../components/Leaderboard/Challenges';
import { useTranslation } from 'react-i18next';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import Tabs from '../components/UIKit/Tabs';
import Button from '../components/UIKit/Button';

const Leaderboard = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation(['leaderboard', 'common']);

  // Состояние для данных
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courses, setCourses] = useState([]);
  const [activeTab, setActiveTab] = useState('leaderboard');

  // Состояние для обновления рейтинга (для админов)
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);

  // Состояние для фильтрации и пагинации
  const [filters, setFilters] = useState({
    timeFrame: 'all-time',
    page: 1,
    limit: 12,
    filter: 'all',
    specificId: null,
    roleFilter: 'all',
    searchQuery: '',
    excludeInactive: true,
  });

  const [pagination, setPagination] = useState({
    total: 0,
    pages: 1,
    page: 1,
    limit: 12,
  });

  // Состояние для фильтров
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const isFirstRenderRef = useRef(true);

  // Загрузка данных рейтинга с мемоизацией фильтров для предотвращения лишних повторных рендеров
  const fetchLeaderboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await cachedLeaderboardAPI.getLeaderboard(filters);

      setUsers(response.data.users);
      setPagination(response.data.pagination);

      setLoading(false);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError(t('leaderboard:errorLoading'));
      setLoading(false);
    }
  }, [filters, t]);

  // Функция для обновления рейтинга (только для админов)
  const handleUpdateLeaderboard = useCallback(async () => {
    setLoadingLeaderboard(true);
    try {
      await leaderboardAPI.adminUpdateLeaderboard();
      toast.success(t('leaderboard:updateButton.success'));
      // Перезагружаем данные рейтинга после обновления
      fetchLeaderboard();
    } catch (error) {
      console.error('Ошибка при обновлении рейтинга:', error);
      toast.error(t('leaderboard:updateButton.error'));
    } finally {
      setLoadingLeaderboard(false);
    }
  }, [fetchLeaderboard, t]);

  // Загрузка списка курсов для фильтра
  const loadCourses = useCallback(async () => {
    try {
      const response = await coursesAPI.getAll();
      setCourses(response.data);
    } catch (err) {
      console.error('Error loading courses for filter:', err);
    }
  }, []);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);


  // Загрузка данных при первом рендере и изменении зависимостей fetchLeaderboard
  useEffect(() => {
    let isMounted = true;

    // Запрашиваем данные только если компонент смонтирован
    if (isMounted) {
      fetchLeaderboard();
    }

    // Функция очистки для предотвращения обновления состояния размонтированного компонента
    return () => {
      isMounted = false;
    };
  }, [fetchLeaderboard]);

  // Функция для изменения фильтра с дебаунсингом поиска
  const handleFilterChange = useCallback((key, value) => {
    // Если это поисковый запрос, обработаем его отдельно
    if (key === 'searchQuery') {
      // Просто устанавливаем значение без немедленного обновления
      setFilters((prevFilters) => ({
        ...prevFilters,
        [key]: value,
      }));
    } else {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [key]: value,
        // Сбрасываем страницу на первую при изменении фильтров
        ...(key !== 'page' && { page: 1 }),
      }));
    }
  }, []);

  // Функция для сброса фильтров
  const resetFilters = useCallback(() => {
    setFilters({
      timeFrame: 'all-time',
      page: 1,
      limit: 20,
      filter: 'all',
      specificId: null,
      roleFilter: 'all',
      searchQuery: '',
      excludeInactive: true,
    });
    // Также очищаем кэш при сбросе фильтров
    cachedLeaderboardAPI.clearCache();
  }, []);

  // Функция для переключения страницы
  const handlePageChange = useCallback((page) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page,
    }));
  }, []);

  // Функция для переключения вкладок
  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  // Функция для переключения расширения фильтров
  const toggleFiltersExpanded = useCallback(() => {
    setFiltersExpanded((prev) => !prev);
  }, []);

  // Функция для проверки сервера
  const checkServerEndpoints = useCallback(async () => {
    try {
      // Проверяем доступность основных API-эндпоинтов
      console.log('Проверка доступности API-эндпоинтов...');

      if (activeTab === 'personal' && user) {
        try {
          console.log(`Проверка эндпоинта /leaderboard/user/${user._id}...`);
          // Используем обычный API, а не кэшированный, так как это проверочный запрос
          await leaderboardAPI.getUserRanking(user._id, 5);
          console.log('API персонального рейтинга доступно');
        } catch (err) {
          console.error('Ошибка при вызове getUserRanking:', err);
          // Не устанавливаем ошибку, так как обработка уже есть в компоненте PersonalRanking
        }
      }
    } catch (error) {
      console.error('Ошибка при проверке API-эндпоинтов:', error);
    }
  }, [activeTab, user]);

  useEffect(() => {
    // Выполняем проверку только один раз при активации вкладки "Персональный рейтинг"
    if (user && activeTab === 'personal' && isFirstRenderRef.current) {
      checkServerEndpoints();
      isFirstRenderRef.current = false;
    }
  }, [activeTab, user, checkServerEndpoints]);

  // Проверка, является ли пользователь администратором
  const isAdmin = useMemo(() => user && user.userMeta.roles.includes('admin'), [user]);
  // Проверка, имеет ли пользователь доступ к вызовам (учитель или админ)
  const hasChallengesAccess = useMemo(
    () => user && (user.userMeta.roles.includes('admin') || user.userMeta.roles.includes('teacher')),
    [user]
  );

  return (
    <Container sx={{ mt: 4, px: isMobile ? 1 : 3 }}>
      {/* Заголовок страницы */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography
          variant={isMobile ? 'h4' : 'h3'}
          component="h1"
          display="flex"
          alignItems="center"
          gap={1}
        >
          <EmojiEventsIcon sx={{ fontSize: isMobile ? '1.8rem' : '2.2rem' }} />
          {t('leaderboard:title')}
        </Typography>

        {/* Кнопка обновления рейтинга (только для администраторов) */}
        {isAdmin && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateLeaderboard}
            disabled={loadingLeaderboard}
            startIcon={
              loadingLeaderboard && (
                <CircularProgress size={20} color="inherit" />
              )
            }
          >
            {loadingLeaderboard
              ? t('leaderboard:updateButton.updating')
              : t('leaderboard:updateButton.update')}
          </Button>
        )}
      </Box>

      {/* Вкладки */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: { xs: '12px', sm: '16px' },
          p: '4px',
          width: '100%',
          overflow: 'hidden',
          mb: 3,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          swapBackground
        >
          <Tab
            icon={<GroupIcon />}
            label={t('leaderboard:tabs.global')}
            value="leaderboard"
          />
          {user && (
            <Tab
              icon={<PersonIcon />}
              label={t('leaderboard:tabs.personal')}
              value="personal"
            />
          )}
          <Tab
            icon={<TrendingUpIcon />}
            label={t('leaderboard:tabs.competitions')}
            value="competitions"
          />
          {user && hasChallengesAccess && (
            <Tab
              icon={<SportsKabaddiIcon />}
              label={t('leaderboard:tabs.challenges')}
              value="challenges"
            />
          )}
        </Tabs>
      </Box>

      {/* Содержимое вкладки "Глобальный рейтинг" */}
      {activeTab === 'leaderboard' && (
        <>
          {/* Фильтры */}
          <LeaderboardFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={resetFilters}
            courses={courses}
            expanded={filtersExpanded}
            onExpandToggle={toggleFiltersExpanded}
          />

          {/* Список пользователей */}
          <UsersList
            users={users}
            loading={loading}
            error={error}
            currentUserId={user?._id}
            timeFrame={filters.timeFrame}
            pagination={pagination}
            onPageChange={handlePageChange}
          />
        </>
      )}

      {/* Содержимое вкладки "Персональный рейтинг" */}
      {activeTab === 'personal' && user && (
        <>
          {console.log('Рендеринг персонального рейтинга, пользователь:', user)}
          {error ? (
            <Box my={3}>
              <Alert severity="warning">{error}</Alert>
            </Box>
          ) : (
            <PersonalRanking currentUser={user} />
          )}
        </>
      )}

      {/* Содержимое вкладки "Соревнования" */}
      {activeTab === 'competitions' && <Competitions currentUser={user} />}

      {/* Содержимое вкладки "Вызовы" - только для учителей и администраторов */}
      {activeTab === 'challenges' && hasChallengesAccess && (
        <Challenges currentUser={user} />
      )}
    </Container>
  );
};

export default Leaderboard;
